/**
 * Campaign brief (Messaging OS, meeting 2026-09-18).
 *
 * Owner rule: the instructions that drive a campaign's messages are never
 * hidden. During campaign creation the user is interviewed (offer, proof,
 * FAQs, ideal customer, tone), the answers become a campaign brief, and the
 * EXACT text the AI receives is shown to them to edit and confirm before the
 * campaign can send.
 *
 * This module is the single source of truth for that text. The dashboard
 * renders the preview with renderCampaignBrief() and the AI worker renders
 * the runtime prompt section with the same function, so what the user
 * confirms is byte-for-byte what the model gets.
 *
 * - Every field is optional except what_you_sell and who_its_for. Empty
 *   fields and empty sections are left out of the prompt entirely, so the
 *   model never sees a blank heading it might try to fill in.
 * - Rules are additive toggles. Turning one off only drops that line; it
 *   can never switch off the SKILL.md or honesty rules, which always apply.
 * - Free text is run through sanitizeUserPromptInput before rendering (same
 *   hygiene as every other user field that reaches a prompt).
 */

import { sanitizeUserPromptInput } from './prompt-sanitize'

export const CAMPAIGN_BRIEF_VERSION = 1 as const
export const MAX_BRIEF_FAQS = 10
/** Prompt versions kept per campaign (campaign_prompt_versions). */
export const MAX_BRIEF_VERSIONS_KEPT = 10

export type CampaignBriefFaq = {
  readonly question: string
  readonly answer: string
}

export type CampaignBrief = {
  readonly version: typeof CAMPAIGN_BRIEF_VERSION
  /** What do you sell? (required) */
  readonly what_you_sell: string
  /** Who is it for? (required) */
  readonly who_its_for: string
  /** The result a customer gets. */
  readonly outcome: string
  /** Real proof: numbers, named clients, case studies. */
  readonly proof: string
  /** Why you and not the alternatives. */
  readonly differentiator: string
  /** What the AI may say about price. */
  readonly pricing: string
  /** Ideal customer: roles / titles. */
  readonly ideal_roles: string
  /** Ideal customer: companies (industry, size, stage). */
  readonly ideal_companies: string
  /** Signs a lead is a great fit. */
  readonly must_haves: string
  /** Signs a lead is NOT a fit. */
  readonly disqualifiers: string
  /** FAQs and objections with the answer the user wants given. */
  readonly faqs: readonly CampaignBriefFaq[]
  /** What a good conversation ends with. */
  readonly conversation_goal: string
  /** How messages should sound. */
  readonly tone: string
  /** Words, claims or topics to never use. */
  readonly never_say: string
  /** Anything else. */
  readonly extra_instructions: string
  /** Rule id -> on/off. Missing ids fall back to the rule's default. */
  readonly rules: Readonly<Record<string, boolean>>
}

export type CampaignBriefTextField = Exclude<keyof CampaignBrief, 'version' | 'faqs' | 'rules'>

/** Per-field length caps. Generous for people, bounded for the prompt. */
export const BRIEF_FIELD_MAX: Record<CampaignBriefTextField, number> = {
  what_you_sell: 600,
  who_its_for: 600,
  outcome: 600,
  proof: 1200,
  differentiator: 600,
  pricing: 400,
  ideal_roles: 400,
  ideal_companies: 400,
  must_haves: 600,
  disqualifiers: 600,
  conversation_goal: 400,
  tone: 400,
  never_say: 600,
  extra_instructions: 1500,
}
export const FAQ_QUESTION_MAX = 300
export const FAQ_ANSWER_MAX = 800

export const REQUIRED_BRIEF_FIELDS: readonly CampaignBriefTextField[] = ['what_you_sell', 'who_its_for']

export type BriefRule = {
  readonly id: string
  readonly label: string
  /** Shown on hover: why the rule exists. */
  readonly rationale: string
  /** The line added to the prompt when the rule is on. */
  readonly instruction: string
  readonly defaultOn: boolean
}

/**
 * Toggleable rules. All ADD a constraint; none relaxes SKILL.md or the
 * honesty rules. Keep ids stable: they are stored in saved briefs.
 */
export const BRIEF_RULES: readonly BriefRule[] = [
  {
    id: 'no_superlatives',
    label: 'Avoid superlatives and hype',
    rationale: 'Words like "best", "leading" or "game-changing" read like an ad and make people stop reading.',
    instruction: 'Avoid superlatives and hype words (best, leading, world-class, revolutionary, game-changing, cutting-edge).',
    defaultOn: true,
  },
  {
    id: 'no_exclamation',
    label: 'No exclamation marks',
    rationale: 'Exclamation marks in cold messages come across as salesy and over-eager.',
    instruction: 'Do not use exclamation marks.',
    defaultOn: true,
  },
  {
    id: 'plain_language',
    label: 'Plain words, no jargon',
    rationale: 'Leads skim on a phone. Jargon and acronyms they do not use themselves slow them down.',
    instruction: 'Use plain everyday words. No industry jargon, buzzwords or acronyms the lead would not use themselves.',
    defaultOn: true,
  },
  {
    id: 'one_question',
    label: 'At most one question per message',
    rationale: 'One clear question is easier to answer than two. Replies go up when the ask is simple.',
    instruction: 'Ask at most one question per message.',
    defaultOn: false,
  },
  {
    id: 'short_messages',
    label: 'Keep messages very short',
    rationale: 'Shorter messages get read in the notification preview. Useful for busy, senior audiences.',
    instruction: 'Keep every message under 300 characters.',
    defaultOn: false,
  },
  {
    id: 'no_price_in_messages',
    label: 'Never quote prices in messages',
    rationale: 'Some offers are priced per customer. A number out of context can end the conversation early.',
    instruction: 'Never state a price or number for cost in a message. If the lead asks, say pricing depends on their situation and offer to go through it together.',
    defaultOn: false,
  },
  {
    id: 'no_competitor_mentions',
    label: 'Never mention competitors',
    rationale: 'Naming competitors invites comparisons you do not control and can sound defensive.',
    instruction: 'Never name or compare against competitors.',
    defaultOn: false,
  },
] as const

function str(v: unknown, max: number): string {
  if (typeof v !== 'string') return ''
  return v.replace(/\r\n/g, '\n').trim().slice(0, max)
}

/** An empty brief with every rule at its default. */
export function emptyCampaignBrief(): CampaignBrief {
  const rules: Record<string, boolean> = {}
  for (const r of BRIEF_RULES) rules[r.id] = r.defaultOn
  return {
    version: CAMPAIGN_BRIEF_VERSION,
    what_you_sell: '',
    who_its_for: '',
    outcome: '',
    proof: '',
    differentiator: '',
    pricing: '',
    ideal_roles: '',
    ideal_companies: '',
    must_haves: '',
    disqualifiers: '',
    faqs: [],
    conversation_goal: '',
    tone: '',
    never_say: '',
    extra_instructions: '',
    rules,
  }
}

/**
 * Lenient parse of anything (stored JSON, a request body) into a clean
 * brief: unknown keys dropped, every field clamped, blank FAQs removed,
 * unknown rule ids dropped. Returns null when the value is not an object.
 */
export function normalizeCampaignBrief(value: unknown): CampaignBrief | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null
  const v = value as Record<string, unknown>
  const base = emptyCampaignBrief()
  const text = {} as Record<CampaignBriefTextField, string>
  for (const key of Object.keys(BRIEF_FIELD_MAX) as CampaignBriefTextField[]) {
    text[key] = str(v[key], BRIEF_FIELD_MAX[key])
  }
  const faqs: CampaignBriefFaq[] = []
  if (Array.isArray(v.faqs)) {
    for (const f of v.faqs) {
      if (faqs.length >= MAX_BRIEF_FAQS) break
      const e = f as { question?: unknown; answer?: unknown } | null
      if (typeof e !== 'object' || e === null) continue
      const question = str(e.question, FAQ_QUESTION_MAX)
      const answer = str(e.answer, FAQ_ANSWER_MAX)
      if (question && answer) faqs.push({ question, answer })
    }
  }
  const rules: Record<string, boolean> = { ...base.rules }
  if (typeof v.rules === 'object' && v.rules !== null) {
    for (const r of BRIEF_RULES) {
      const on = (v.rules as Record<string, unknown>)[r.id]
      if (typeof on === 'boolean') rules[r.id] = on
    }
  }
  return { ...base, ...text, faqs, rules }
}

/** Required fields that are still empty. */
export function missingBriefFields(brief: CampaignBrief): CampaignBriefTextField[] {
  return REQUIRED_BRIEF_FIELDS.filter((k) => brief[k].trim() === '')
}

function clean(s: string, max: number): string {
  return sanitizeUserPromptInput(s, { maxLength: max })
}

/**
 * The exact prompt section the AI receives for this campaign. Empty fields
 * and sections are omitted. Returns '' when the brief carries nothing.
 */
export function renderCampaignBrief(brief: CampaignBrief): string {
  const sections: string[] = []
  const add = (heading: string, body: string) => {
    if (body.trim()) sections.push(`### ${heading}\n${body.trim()}`)
  }
  const f = (k: CampaignBriefTextField) => clean(brief[k], BRIEF_FIELD_MAX[k])

  add('What we sell', f('what_you_sell'))
  add('Who it is for', f('who_its_for'))
  add('The result customers get', f('outcome'))
  add('Proof you may cite (use only this, never invent more)', f('proof'))
  add('What makes us different', f('differentiator'))
  add('What you may say about price', f('pricing'))

  const ideal = [
    f('ideal_roles') ? `- Roles: ${f('ideal_roles')}` : '',
    f('ideal_companies') ? `- Companies: ${f('ideal_companies')}` : '',
    f('must_haves') ? `- Great fit when: ${f('must_haves')}` : '',
  ].filter(Boolean).join('\n')
  add('Ideal customer', ideal)
  add(
    'Not a fit (if the lead clearly matches this, do not pitch; stay polite and brief)',
    f('disqualifiers'),
  )

  if (brief.faqs.length > 0) {
    const faqText = brief.faqs
      .map((q) => `Q: ${clean(q.question, FAQ_QUESTION_MAX)}\nA: ${clean(q.answer, FAQ_ANSWER_MAX)}`)
      .join('\n\n')
    add('FAQs and objections (answer in your own words, keep the facts)', faqText)
  }

  add('How a good conversation ends', f('conversation_goal'))
  add('Tone', f('tone'))
  add('Never say', f('never_say'))
  add('Extra instructions', f('extra_instructions'))

  const ruleLines = BRIEF_RULES.filter((r) => brief.rules[r.id] ?? r.defaultOn).map((r) => `- ${r.instruction}`)
  add('Rules for this campaign', ruleLines.join('\n'))

  if (sections.length === 0) return ''
  return [
    '## CAMPAIGN BRIEF (written and confirmed by the user for this campaign)',
    'This brief is the source of truth for what this campaign sells, to whom, and how it should sound. Where it is more specific than the general offer settings above, follow the brief. Never claim anything about the offer that is not written here or in the offer assets. SKILL.md structure, step rules and the honesty rules still apply.',
    '',
    sections.join('\n\n'),
  ].join('\n')
}

/**
 * Brief confirmation gate. Campaigns created since this feature carry
 * settings.brief_required = true and may not start sending until the user
 * has confirmed a brief. Legacy campaigns (no flag) are unaffected.
 * Returns an error message, or null when the campaign may send.
 */
export function campaignBriefGate(settings: unknown): string | null {
  let s: Record<string, unknown> = {}
  if (typeof settings === 'string') {
    try {
      s = JSON.parse(settings) as Record<string, unknown>
    } catch {
      return null
    }
  } else if (typeof settings === 'object' && settings !== null) {
    s = settings as Record<string, unknown>
  }
  if (s.brief_required !== true) return null
  const brief = normalizeCampaignBrief(s.brief)
  if (!brief || missingBriefFields(brief).length > 0) {
    return 'This campaign needs its messaging brief filled in (what you sell and who it is for) before it can send.'
  }
  if (typeof s.brief_confirmed_at !== 'string' || !s.brief_confirmed_at) {
    return 'Review and confirm this campaign\'s messaging brief before it can send.'
  }
  return null
}
